import { useState } from "react";
import { ChevronDown } from "lucide-react";
import FAQSchema from "@/components/schemas/FAQSchema";

const homeFAQs = [
  {
    question: "Why do traditional contracts often fail to deliver real business results?",
    answer: "Traditional cost-plus based contracts pay for effort, hours, scope, and deliverables — not the business outcome that actually matters. Organisations end up buying work, not results. This leads to misaligned incentives, scope-and-hope delivery, weak accountability, and wasted spend on work that is technically complete but commercially useless."
  },
  {
    question: "What is outcome-based contracting?",
    answer: "Outcome-based contracting ties payment to the achievement of a defined business result — uptime, throughput, cost reduction, productivity gain — rather than to the completion of a scope. The supplier assumes a share of the performance risk. The buyer pays for what they actually need."
  },
  {
    question: "How is performance-based different from SLA-based contracting?",
    answer: "SLA-based contracts measure operational KPI — availability, response time, delivery rate. Performance-based contracts go one level deeper: they measure business outputs and the impact on outcomes. Not \"was the machine available?\" but \"did the machine produce the required output at the required cost?\" The distinction determines who bears the risk and who captures the upside."
  },
  {
    question: "Where do the macro-tracks fit in a typical engagement?",
    answer: "Most clients begin with the Audit phase — a diagnostic that identifies which procurement categories are ready to shift and what performance model is appropriate. The Solution Design phase follows with the contracting architecture. Implementation deploys it. Performance Management sustains it. Each phase can be purchased independently, but they are designed to compound."
  },
  {
    question: "Do you work with organizations outside Europe?",
    answer: "The consulting practice is focused on Europe, where performance-based procurement frameworks are being developed and locations where the policy environment is most supportive. If your organization is based elsewhere but operating in these markets, we can discuss fit on a discovery call."
  },
  {
    question: "What size organization do you typically work with?",
    answer: "The method is designed for industrial SMEs and mid-market organisations — typically 50 to 5,000 employees — in manufacturing, logistics, energy, or supply chain. Large enterprises with established procurement functions are welcome, but the Engines are calibrated for organisations where the leadership team is still close to the procurement decisions."
  }
];

const HomeFAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-20 relative">
      <FAQSchema faqs={homeFAQs} pageUrl="https://klexu.com/" />
      
      <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Get answers to common questions about our services and the machine economy
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {homeFAQs.map((faq, index) => (
            <div
              key={index}
              className="glass-card border-white/10 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
              >
                <span className="font-medium pr-4">{faq.question}</span>
                <ChevronDown
                  size={20}
                  className={`flex-shrink-0 transition-transform duration-200 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`px-6 overflow-hidden transition-all duration-200 ${
                  openIndex === index ? "pb-4 max-h-96" : "max-h-0"
                }`}
              >
                <p className="text-muted-foreground">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeFAQ;
